import { Fragment } from "react";
import { Checkbox, Text } from "@mantine/core";
import { getColValue as getDisplayValue } from "../../../pages/Reports/Report/Report.utils";
import { useSelector } from "../../../redux/hooks";
import {
  Flex,
  StyledSpaceCell,
  StyledTable,
  StyledTd,
  StyledTh,
  StyledTr,
} from "../../../styled";
import { ICol, IRow } from "src/interfaces/Logbooks";
import { previewItem } from "./NMEAAmendModal";
import styles from "./NMEAPreview.module.scss";
import {
  getColValueString,
  getComparableNMEACols,
  isTimeColumn,
  renderBoldTimeValue,
  renderNMEACellValue,
} from "./NMEAPreviewShared";

const getAllColValues = (row: IRow): any[] => {
  const cols = row.cols ? Object.values(row.cols) : [];
  const subrowCols =
    row.subrows?.flatMap((sr: any) => Object.values(sr.cols ?? {})) ?? [];
  return [...cols, ...subrowCols];
};

interface Props {
  previewItems: previewItem[];
  selectedRows: Record<string, boolean>;
  onToggleRow: (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => void;
  onToggleSection: (sectionIndex: number, checked: boolean) => void;
}

const NMEAAmendModalPreview = ({
  previewItems,
  selectedRows,
  onToggleRow,
  onToggleSection,
}: Props) => {
  const groupedBySection = previewItems.reduce(
    (acc, item) => {
      const { sectionIndex } = item;
      if (!acc[sectionIndex]) {
        acc[sectionIndex] = [];
      }
      acc[sectionIndex].push(item);
      return acc;
    },
    {} as Record<number, previewItem[]>,
  );

  return (
    <Flex direction="column" gap={24}>
      {Object.entries(groupedBySection).map(([sectionIndex, items]) => {
        const sectionName = items[0]?.sectionName;
        const headers = Object.entries(items[0]?.nmeaRow?.cols || {});
        return (
          <Flex direction="column" gap={20} style={{ width: "100%" }}>
            <NMEAAmendModalPreviewSection
              key={sectionIndex}
              sectionName={sectionName}
              items={items}
              headers={headers}
              sectionIndex={Number(sectionIndex)}
              selectedRows={selectedRows}
              onToggleRow={onToggleRow}
              onToggleSection={onToggleSection}
            />
          </Flex>
        );
      })}
    </Flex>
  );
};

const NMEAAmendModalPreviewSection = ({
  items,
  sectionName,
  headers,
  sectionIndex,
  selectedRows,
  onToggleRow,
  onToggleSection,
}: {
  items: previewItem[];
  sectionName?: string | null;
  headers: [string, ICol][];
  sectionIndex: number;
  selectedRows: Record<string, boolean>;
  onToggleRow: (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => void;
  onToggleSection: (sectionIndex: number, checked: boolean) => void;
}) => {
  const { allSelected, someSelected } = items.reduce(
    (acc, item) => {
      const isSelected = selectedRows[`${item.sectionIndex}-${item.rowIndex}`];
      if (isSelected) {
        acc.allSelected = acc.allSelected && true;
        acc.someSelected = true;
      } else {
        acc.allSelected = false;
      }
      return acc;
    },
    { allSelected: true, someSelected: false },
  );
  return (
    <Flex direction="column" style={{ width: "100%" }}>
      {sectionName && (
        <Text
          className="sticky-section-header"
          fz={13}
          c="var(--mantine-color-dimmed)"
          tt="uppercase"
          style={{ marginBottom: 0 }}>
          {sectionName}
        </Text>
      )}
      <StyledTable key={sectionIndex} style={{ border: "none" }}>
        <StyledTr
          style={{
            position: "sticky",
            top: sectionName ? 25 : 0,
            zIndex: 1,
          }}>
          {headers.map(([nmeaKey, nmeaCol]) => {
            return (
              <StyledTh
                key={`header-${nmeaKey}`}
                style={{ whiteSpace: "nowrap" }}>
                {nmeaCol.name?.replaceAll("<br>", " ") ?? nmeaKey}
              </StyledTh>
            );
          })}
          <StyledTh>
            <Flex justify="center" align="center" w="100%" h="100%" px={4}>
              <Checkbox
                size="sm"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={event =>
                  onToggleSection(sectionIndex, event.currentTarget.checked)
                }
              />
            </Flex>
          </StyledTh>
        </StyledTr>

        {items.map((item, i) => (
          <NMEAAmendModalPreviewRow
            key={`${sectionIndex}-${item.rowIndex}`}
            previewIndex={i}
            selected={
              selectedRows[`${item.sectionIndex}-${item.rowIndex}`] ?? false
            }
            onToggleRow={onToggleRow}
            {...item}
          />
        ))}
      </StyledTable>
    </Flex>
  );
};

const NMEAAmendModalPreviewRow = ({
  logbookRow,
  nmeaRow,
  previewIndex,
  sectionIndex,
  rowIndex,
  selected,
  onToggleRow,
}: {
  logbookRow: IRow;
  nmeaRow: previewItem["nmeaRow"];
  previewIndex: number;
  sectionIndex: number;
  rowIndex: number;
  selected: boolean;
  onToggleRow: (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => void;
}) => {
  const { isBridgeLog } = useSelector(s => s.generatorSlice.lbentry);
  const nmeaId = nmeaRow.NMEAId;

  const manualColsByNMEAKey: Record<string, any> = {};
  getAllColValues(logbookRow).forEach((col: any) => {
    if (col?.NMEA) {
      manualColsByNMEAKey[col.NMEA] = col;
    }
  });

  const comparableCols = getComparableNMEACols(nmeaRow);

  const highlightColor = "var(--mantine-color-green-light)";

  const handleRowClick = () => {
    onToggleRow(sectionIndex, rowIndex, !selected);
  };

  return (
    <Fragment>
      {previewIndex !== 0 && (
        <StyledSpaceCell
          colSpan={comparableCols.length}
          style={{ height: 20 }}
        />
      )}

      <tbody className={styles.previewRowGroup} onClick={handleRowClick}>
        <StyledTr key={`${nmeaId}-manual`}>
          {comparableCols.map(([nmeaKey]) => {
            const manualCol = manualColsByNMEAKey[nmeaKey];
            const manualValue = getDisplayValue(manualCol);
            const content = isTimeColumn(nmeaKey, manualCol)
              ? renderBoldTimeValue(manualValue)
              : manualValue;
            return (
              <StyledTd
                key={`${nmeaId}-manual-${nmeaKey}`}
                style={{
                  whiteSpace: "nowrap",
                  width: isTimeColumn(nmeaKey, manualCol) ? 1 : "auto",
                }}>
                {content}
              </StyledTd>
            );
          })}
          <StyledTd rowSpan={2} width="1px" onClick={e => e.stopPropagation()}>
            <Flex justify="center" align="center" w="100%" h="100%" px={4}>
              <Checkbox
                size="sm"
                checked={selected}
                onChange={event =>
                  onToggleRow(
                    sectionIndex,
                    rowIndex,
                    event.currentTarget.checked,
                  )
                }
              />
            </Flex>
          </StyledTd>
        </StyledTr>

        <StyledTr key={`${nmeaId}-nmea`}>
          {comparableCols.map(([nmeaKey, nmeaCol]) => {
            const manualCol = manualColsByNMEAKey[nmeaKey];
            const manualValue = getColValueString(manualCol ?? "");
            const nmeaValue = getColValueString(nmeaCol ?? "");
            const isDifferent = nmeaValue !== manualValue;

            return (
              <StyledTd
                key={`${nmeaId}-nmea-${nmeaKey}`}
                style={{
                  ...(isDifferent
                    ? {
                        backgroundColor: highlightColor,
                      }
                    : undefined),
                  whiteSpace: "nowrap",
                }}>
                {renderNMEACellValue(nmeaRow, nmeaKey, nmeaCol, isBridgeLog)}
              </StyledTd>
            );
          })}
        </StyledTr>
      </tbody>
    </Fragment>
  );
};

export default NMEAAmendModalPreview;
